import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { firstValueFrom, map } from 'rxjs';
import { getAllRouteData } from '../../lib/get-all-route-data';
import { getFullPath } from '../../lib/get-full-path';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { PICFLOW_STORYBLOK } from '../../picflow-storyblok';
import { JsonLDService } from '../../services/json-ld.service';
import { LanguageService } from '../../services/language.service';
import { StoryblokReplaceService } from '../../services/storyblok-replace.service';

export const storyblokPageJsonLDGuard: CanActivateFn = async route => {
  const storyblokService = inject(PICFLOW_STORYBLOK);
  const jsonLDService = inject(JsonLDService);
  const config = inject(PICFLOW_CONFIG);
  const replaceService = inject(StoryblokReplaceService);
  const languageService = inject(LanguageService);

  const path = getAllRouteData(route).storyblokPathOverride || getFullPath(route).join('/') || 'home';

  const story = await firstValueFrom(
    storyblokService.getStory(path, route).pipe(map(resp => (resp && resp.story) || (false as const)))
  );

  if (!story) return true;
  const pageType = story.content.jsonLdPageType || 'WebPage';

  const title = replaceService.replaceText(story.content.metaTitle) || story.name;
  const description = replaceService.replaceText(story.content.metaDescription) || undefined;
  const slug = story.full_slug.replace(/\/$/, '');
  const url = slug === 'home' ? config.correctPicflowUrl : `${config.correctPicflowUrl}/${slug}`;
  const inLanguage = await firstValueFrom(languageService.currentLanguage$);

  jsonLDService.add('page', {
    '@context': 'https://schema.org',
    '@type': pageType,
    name: title,
    description,
    url,
    inLanguage,
    isPartOf: { '@id': 'https://picflow.com/#website' },
  });

  // TODO: Consider adding BreadcrumbList for pages without a CMS Breadcrumb block (e.g. /blog, /topic/*)

  return true;
};
